import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import * as authService from '../services/authService';
import {
  ACCESS_DENIED_EVENT,
  SESSION_EXPIRED_EVENT,
  accessDeniedMessage,
} from '../services/authService';
import type { AccessDeniedDetail, User, LoginCredentials } from '../services/authService';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * Por que a sessão terminou, quando foi o backend que negou o acesso (prazo
   * vencido, conta bloqueada). A tela de login mostra.
   */
  notice: string | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    const handleSessionExpired = () => {
      setToken(null);
      setUser(null);
    };

    const handleAccessDenied = (event: Event) => {
      handleSessionExpired();
      setNotice(accessDeniedMessage((event as CustomEvent<AccessDeniedDetail>).detail));
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    window.addEventListener(ACCESS_DENIED_EVENT, handleAccessDenied);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
      window.removeEventListener(ACCESS_DENIED_EVENT, handleAccessDenied);
    };
  }, []);

  const checkAuth = () => {
    setIsLoading(true);
    try {
      const isValid = authService.isAuthenticated();

      if (isValid) {
        const storedToken = authService.getStoredToken();
        const storedUser = authService.getStoredUser();

        if (storedToken && storedUser) {
          setToken(storedToken);
          setUser(storedUser);
        }
      } else {
        setToken(null);
        setUser(null);
      }
    } catch (error) {
      console.error('Erro ao verificar autenticação:', error);
      authService.logout();
      setToken(null);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials: LoginCredentials): Promise<void> => {
    setNotice(null);
    try {
      const response = await authService.login(credentials);
      setToken(response.access_token);
      setUser(response.user);
    } catch (error) {
      if (error instanceof Error && error.message.includes('expirada')) {
        logout();
      }
      throw error;
    }
  };

  const logout = () => {
    authService.logout();
    setToken(null);
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    isLoading,
    notice,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};
